import { z } from "zod";
import { IntentClaimSchema } from "./claim.ts";
import { DiffSummarySchema } from "./git.ts";
import { LandRequestSchema, MigrationLaneSchema } from "./land.ts";
import { MessageSchema } from "./message.ts";
import { OverlapSchema } from "./overlap.ts";
import { PlanAmendmentSchema, WorkPackageSchema, WorkPlanSchema } from "./plan.ts";
import { IdSchema, TimestampSchema } from "./primitives.ts";
import { ResolutionSchema } from "./resolution.ts";
import { AgentRunSchema } from "./run.ts";
import { SessionSchema } from "./session.ts";
import { DeployRecordSchema } from "./stack.ts";
import { VerdictSchema } from "./verdict.ts";

const envelope = {
  /** Event id, unique. */
  id: IdSchema,
  /** Position in the coordinator's event log: starts at 1, strictly increasing, no gaps. Use it to resume a stream. */
  seq: z.number().int().positive(),
  /** When the change happened. */
  ts: TimestampSchema,
};

function event<T extends string, D extends z.ZodType>(type: T, data: D) {
  return z.object({ ...envelope, type: z.literal(type), data });
}

/**
 * Every state change the coordinator makes, as one discriminated union on `type`.
 * The dashboard renders these; the event log of a run replays as a recording.
 *
 * Every `data` carries the full current record, so a consumer never needs a follow-up read.
 */
export const PlatformEventSchema = z.discriminatedUnion("type", [
  /** A session registered. */
  event("session.registered", z.object({ session: SessionSchema })),
  /** A session heartbeated. Carries only what changed, to keep the stream light. */
  event("session.heartbeat", z.object({ sessionId: IdSchema, lastHeartbeat: TimestampSchema, note: z.string().nullable() })),
  /** A session's status changed (idle, active again, landed, expired). */
  event("session.updated", z.object({ session: SessionSchema })),
  /** A claim was declared. `overlapIds` lists the overlaps it raised. */
  event("claim.declared", z.object({ claim: IntentClaimSchema, overlapIds: z.array(IdSchema) })),
  /** A claim changed: released, expired, promoted from the migration queue, or narrowed or relaxed by a resolution. */
  event("claim.updated", z.object({ claim: IntentClaimSchema })),
  /** A new overlap was detected. */
  event("overlap.detected", z.object({ overlap: OverlapSchema })),
  /** An overlap's status changed. */
  event("overlap.updated", z.object({ overlap: OverlapSchema })),
  /** A message was posted. */
  event("message.posted", z.object({ message: MessageSchema })),
  /** A resolution was recorded (and is now enforced). */
  event("resolution.recorded", z.object({ resolution: ResolutionSchema })),
  /** The migration lane changed hands or its queue changed. */
  event("migration_lane.updated", z.object({ lane: MigrationLaneSchema })),
  /** A work plan was submitted (or a draft resubmitted). */
  event("plan.submitted", z.object({ plan: WorkPlanSchema })),
  /** A work plan was approved. */
  event("plan.approved", z.object({ plan: WorkPlanSchema })),
  /** An approved plan was amended (contracts-v3): packages added, superseded or rewired. Each changed package also gets `package.updated`. */
  event("plan.amended", z.object({ plan: WorkPlanSchema, amendment: PlanAmendmentSchema })),
  /** A work package changed status (dispatched, in progress, verifying, landed, failed) or was assigned. */
  event("package.updated", z.object({ package: WorkPackageSchema })),
  /** A diff summary was ingested. `overlapIds` lists the diff overlaps it raised. */
  event("diff.ingested", z.object({ diff: DiffSummarySchema, overlapIds: z.array(IdSchema) })),
  /** A verdict was recorded. */
  event("verdict.recorded", z.object({ verdict: VerdictSchema })),
  /** A land request was created or changed status. */
  event("land.updated", z.object({ land: LandRequestSchema })),
  /** A deploy record was created or changed, including promotion approval. */
  event("deploy.updated", z.object({ deploy: DeployRecordSchema })),
  /** An agent run started or changed status (runner WP-17). */
  event("run.updated", z.object({ run: AgentRunSchema })),
]);
/** See {@link PlatformEventSchema}. */
export type PlatformEvent = z.infer<typeof PlatformEventSchema>;

/** Every `type` a {@link PlatformEvent} can have. */
export const PLATFORM_EVENT_TYPES = PlatformEventSchema.options.map((o) => o.shape.type.value);
/** One of {@link PLATFORM_EVENT_TYPES}. */
export type PlatformEventType = PlatformEvent["type"];
/** The event of a given type. */
export type PlatformEventOf<T extends PlatformEventType> = Extract<PlatformEvent, { type: T }>;
/** A {@link PlatformEventType} schema, for validating filters. */
export const PlatformEventTypeSchema = z.enum(PLATFORM_EVENT_TYPES as [PlatformEventType, ...PlatformEventType[]]);
